import React, { useState, useEffect, useRef } from 'react';
import { RouteComponentProps } from 'react-router';

import PageWrapper from './PageWrapper';
import Page from './Page';

import { SailingList, SailingProvider, MenuBar, TemplateLinkForm } from '@go-client/components';
import {
  BubbleDrawer,
  BubbleOverlay,
  BubbleInput,
  BubbleButton,
  BubbleDebounce,
  BubbleAccordion,
} from '@go-client/components/BubbleUI';
import { updateTags } from '@go-client/lib/api/property';
import {
  saveSailingDayAsTemplate,
  deleteSailingDayTemplate,
  lockSailingDay,
  unlockSailingDay,
  saveSailingAsTemplate,
  createTemplateFromSailingDay,
  exportSailing,
  getSailing,
  updateSailing,
  publishSailing,
} from '@go-client/lib/api/itinerary';
import {
  BareSimpleSailing,
  SimpleSailing,
  SimpleSailingDay,
  BareSimpleSailingDay,
} from '@go-shared/types/sailing';
import {
  populateSimple as populateSimpleSailing,
  bareSimpleSailingDay,
  bareSimple as bareSimpleSailing,
  unsimplifyBareSailing,
} from '@go-shared/lib/itinerary';

import './Itinerary.scss';

import { SimpleSailingTemplate, SimpleTemplate } from '@go-shared/types/template';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { Popup, Alert } from '@spark-labs/avocado-ui';
import { DropdownItem } from '@spark-labs/avocado-ui/dist/components/Dropdown';
import { SailingProviderChildrenProps } from '@go-client/components/SailingProvider';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import {
  linkTemplateFragments,
  unlinkTemplateFragments,
} from '@go-client/lib/api/templateFragment';
import {
  FRAGMENT_TYPES,
  LinkFragment,
  StrategicLinkFragment,
  TemplateFragmentUnlinkStrategy,
} from '@go-shared/types/templateFragment';
import { FragmentBlockSync } from '@go-client/components/TemplateLinkForm/TemplateLinkForm';
import TemplateForm, { TemplateFormDataBase } from '@go-client/components/TemplateForm';
import qslib from '@go-shared/lib/querystring';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { hasOwnProperty, safeIdCompare } from '@go-shared/lib/utils';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { dateToIsoLocal } from '@go-shared/lib/time';
import { createPortal } from 'react-dom';
import { SailingListProps } from '@go-client/components/SailingList';
import { canEditItineraryInfo, canLockItineraryDay } from '@go-shared/lib/permissions';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  resolveFeedPath,
  resolveItineraryManagementPath,
  resolvePlannerPath,
  resolveTemplateFeedPath,
  resolveTemplatePlannerPath,
} from '@go-client/routes';
import { EditDayForm, EditDaySidePanel } from '@go-client/components/EditDayForm';
import { templateClient } from '@go-client/lib/api/v2/template';
import { safeFetch } from '@go-client/lib/api';
import { TemplateSimpleResponseSchema } from '@go-shared/contracts/template';

interface ItineraryQueryStrings {
  sailing?: string;
  sailingDay?: string;
  date: string;
  daysBefore: number;
  daysAfter: number;
}

function getQueryStrings(): ItineraryQueryStrings {
  const qs = qslib.readQs();

  const sailing =
    hasOwnProperty(qs, 'sailing') && typeof qs.sailing === 'string' ? qs.sailing : undefined;
  const sailingDay =
    hasOwnProperty(qs, 'sailingDay') && typeof qs.sailingDay === 'string'
      ? qs.sailingDay
      : undefined;
  const daysBefore =
    hasOwnProperty(qs, 'daysBefore') && typeof qs.daysBefore === 'number'
      ? qs.daysBefore
      : DEFAULT_SAILING_DAYS_BEFORE;
  const daysAfter =
    hasOwnProperty(qs, 'daysAfter') && typeof qs.daysAfter === 'number'
      ? qs.daysAfter
      : DEFAULT_SAILING_DAYS_AFTER;
  const date =
    hasOwnProperty(qs, 'date') && typeof qs.date === 'string' ? qs.date : DEFAULT_SAILING_DATE;

  return { sailing, sailingDay, daysBefore, daysAfter, date };
}

export interface ItineraryProps {
  propertyId: string;
}

export interface ItineraryRouteParams {
  property: string;
}

interface TemplateBundle {
  sailingTemplates: SimpleSailingTemplate[];
  dayTemplates: SimpleTemplate[];
}

const DEFAULT_SAILING_DATE = dateToIsoLocal();
export const DEFAULT_SAILING_DAYS_BEFORE = 5;
export const DEFAULT_SAILING_DAYS_AFTER = 15;

type ItineraryPageProps = ItineraryProps & RouteComponentProps<ItineraryRouteParams>;

export default function Itinerary(props: ItineraryPageProps) {
  const serverInfo = useServerInfo();
  const [queryStrings, setQueryStrings] = useState<ItineraryQueryStrings>({
    date: DEFAULT_SAILING_DATE,
    daysBefore: DEFAULT_SAILING_DAYS_BEFORE,
    daysAfter: DEFAULT_SAILING_DAYS_AFTER,
  });
  const [templateBundle, setTemplateBundle] = useState<TemplateBundle | undefined>(undefined);

  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedSailing, setSelectedSailing] = useState<SimpleSailing | undefined>(undefined);

  const [showDayTemplateForm, setShowDayTemplateForm] = useState(false);
  const [selectedSailingDay, setSelectedSailingDay] = useState<SimpleSailingDay | undefined>(
    undefined
  );
  const [showDayEditForm, setShowDayEditForm] = useState<boolean>(false);

  const [showCreateTemplateForm, setShowCreateTemplateForm] = useState(false);
  const [createTemplateForm, setCreateTemplateForm] = useState<
    | {
        sailing: BareSimpleSailing;
        sailingDay: BareSimpleSailingDay;
        templateData: TemplateFormDataBase;
      }
    | undefined
  >(undefined);

  const { user } = useUserInfo();
  const { activeProperty, refreshContext: refreshActiveProperty } = useActiveProperty();

  const additionalActionsRef = useRef<HTMLDivElement | null>(null);

  const dayToEdit: EditDaySidePanel | undefined =
    selectedSailing && selectedSailingDay
      ? {
          sailing: bareSimpleSailing(selectedSailing),
          day: bareSimpleSailingDay(selectedSailingDay),
          dayIndex:
            selectedSailing.days.findIndex(d => safeIdCompare(d._id, selectedSailingDay._id)) || 0,
        }
      : undefined;

  useEffect(() => {
    refreshItinerary().catch(console.error);
  }, [activeProperty]);

  async function refreshItinerary() {
    const qs = getQueryStrings();

    const { dayTemplates, sailingTemplates } = await safeFetch(
      templateClient.getTemplates({ query: { propertyId: activeProperty._id } }),
      TemplateSimpleResponseSchema
    );

    setQueryStrings(qs);

    // Casted as any for now so there's no major chamges to the frontend
    setTemplateBundle({
      sailingTemplates: sailingTemplates as any,
      dayTemplates: dayTemplates as any,
    });
  }

  function handleOnAddDaysBefore(amt: number) {
    const newQueryStrings = { ...queryStrings, daysBefore: queryStrings.daysBefore + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function handleOnAddDaysAfter(amt: number) {
    const newQueryStrings = { ...queryStrings, daysAfter: queryStrings.daysAfter + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  async function handleOnChangeDate(iso: string) {
    const newQueryStrings = { ...queryStrings, date: iso };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function handleOnNewSailings(sailings: SimpleSailing[]) {
    if (queryStrings.sailing && queryStrings.sailingDay) {
      const qSailing = sailings.find(s => s._id === queryStrings.sailing);
      const qDay = qSailing
        ? qSailing.days.find(d => d._id === queryStrings.sailingDay)
        : undefined;

      if (qSailing && qDay && !showDayTemplateForm) {
        handleDayTemplateFormClick(qSailing, qDay);
      }
    } else if (queryStrings.sailing) {
      const qSailing = sailings.find(s => s._id === queryStrings.sailing);

      if (qSailing) {
        handleTemplateFormClick(qSailing);
      }
    }

    // update selected sailing and sailing day if different
    if (selectedSailing && selectedSailingDay) {
      const newSailing = sailings.find(s => s._id === selectedSailing._id);
      const newSailingDay = newSailing
        ? newSailing.days.find(d => d._id === selectedSailingDay._id)
        : undefined;

      if (newSailing && newSailingDay) {
        setSelectedSailing(newSailing);
        setSelectedSailingDay(newSailingDay);
      }
    }
  }

  async function handleOnSaveSailing(
    dayEditForm: EditDayForm,
    reloadSailings: () => Promise<void>
  ) {
    if (!dayToEdit) return;

    const newSimpleSailing = { ...dayToEdit.sailing };

    newSimpleSailing.days.splice(dayToEdit.dayIndex, 1, {
      ...dayToEdit.day,
      type: dayEditForm.type,
      locations: dayEditForm.locations.filter(l => l.name.length),
      dayTheme: dayEditForm.dayTheme,
    });

    const daysInDbDict = mapBy((await getSailing(newSimpleSailing._id)).days, '_id');

    const newSailing = unsimplifyBareSailing(
      newSimpleSailing,
      newSimpleSailing.days.map(d => daysInDbDict[d._id].feeds)
    );

    await updateSailing(newSailing._id, newSailing);
    await reloadSailings();
  }

  async function handleOnUnlinkTemplateFragments(
    linkFragments: StrategicLinkFragment[],
    reloadSailings: () => Promise<void>
  ) {
    await unlinkTemplateFragments(activeProperty._id, linkFragments);
    await reloadSailings();
  }

  async function handleOnFinishLinkTemplateFragments(
    linkFragments: LinkFragment[],
    reloadSailings: () => Promise<void>
  ) {
    await linkTemplateFragments(activeProperty._id, linkFragments);
    await reloadSailings();

    handleDayTemplateFormClose();
    handleFormClose();
  }

  async function handleOnCreateTemplateFromSailingDay(templateData: TemplateFormDataBase) {
    if (!createTemplateForm) return;

    await createTemplateFromSailingDay(
      createTemplateForm.sailing._id,
      createTemplateForm.sailingDay._id,
      {
        name: templateData.name,
        tags: templateData.tags.map(t => t._id),
        ...(templateData.dayTheme && { dayTheme: templateData.dayTheme }),
      }
    );

    handleCreateTemplateFormClose();
  }

  function handleOnCreateTemplateFormClick(
    sailing: BareSimpleSailing,
    sailingDay: BareSimpleSailingDay
  ) {
    setShowCreateTemplateForm(true);
    setCreateTemplateForm({
      sailing,
      sailingDay,
      templateData: {
        placeholder: `Copy of ${sailing.name} (Day ${sailingDay.name})`,
        name: '',
        tags: [],
      },
    });
  }

  function handleTemplateFormClick(sailing: SimpleSailing) {
    const newQueryStrings = { ...queryStrings, sailing: sailing._id };

    qslib.writeQs(newQueryStrings);

    setShowTemplateForm(true);
    setSelectedSailing(sailing);
    setQueryStrings(newQueryStrings);
  }

  async function handleExportVoyageData(sailing: SimpleSailing, version: 1 | 2) {
    let result = await exportSailing(sailing.property._id, sailing._id, version);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  function handleDayTemplateFormClick(sailing: SimpleSailing, sailingDay: SimpleSailingDay) {
    const newQueryStrings = { ...queryStrings, sailing: sailing._id, sailingDay: sailingDay._id };

    qslib.writeQs(newQueryStrings);

    setShowDayTemplateForm(true);
    setSelectedSailing(sailing);
    setSelectedSailingDay(sailingDay);
    setQueryStrings(newQueryStrings);
  }

  function handleDayTemplateEditClick(sailing: SimpleSailing, sailingDay: SimpleSailingDay) {
    setSelectedSailing(sailing);
    setSelectedSailingDay(sailingDay);
    setShowDayEditForm(true);
  }

  function handleFormClose() {
    const newQueryStrings = { ...queryStrings };

    delete newQueryStrings.sailing;
    qslib.writeQs(newQueryStrings);

    setShowTemplateForm(false);
    setSelectedSailing(undefined);
    setQueryStrings(newQueryStrings);
  }

  function handleDayTemplateFormClose() {
    const newQueryStrings = { ...queryStrings };

    delete newQueryStrings.sailing;
    delete newQueryStrings.sailingDay;
    qslib.writeQs(newQueryStrings);

    setShowDayTemplateForm(false);
    setSelectedSailingDay(undefined);
    setQueryStrings(newQueryStrings);
  }

  function handleCreateTemplateFormClose() {
    setShowCreateTemplateForm(false);
  }

  function getSailingsComponent(
    sailingProps: SailingProviderChildrenProps,
    sailings: SimpleSailing[]
  ) {
    let sailingDayStarHandler = (sailing: SimpleSailing, sailingDay: SimpleSailingDay) =>
      handleSailingDayStar(sailing, sailingDay, sailingProps.updateSailing);
    let sailingDayLockHandler = (sailing: SimpleSailing, sailingDay: SimpleSailingDay) =>
      handleSailingDayLock(sailing, sailingDay, sailingProps.updateSailing);

    const actionsRef = additionalActionsRef.current;

    const renderCustomActions = actionsRef
      ? (slProps: SailingListProps) =>
          createPortal(
            <>
              <div
                className="grid grid-cols-3 items-stretch rounded-lg overflow-hidden"
                style={{ gridTemplateColumns: 'auto 1fr auto' }}
              >
                <BubbleButton
                  block
                  variant="slate"
                  appearance="squared"
                  onClick={slProps.onAddDaysToBeginningClick}
                  icon="arrow-left"
                />
                <div className="flex items-center justify-center text-white font-semibold bg-slate px-1">
                  Load Days
                </div>
                <BubbleButton
                  block
                  variant="slate"
                  appearance="squared"
                  onClick={slProps.onAddDaysToEndClick}
                  icon="arrow-right"
                />
              </div>
            </>,
            actionsRef
          )
      : undefined;

    return (
      <SailingList
        loading={sailingProps.loading}
        sailings={sailings}
        sailingDetails={sailingProps.sailingDetails}
        showAdminControls={canLockItineraryDay(user)}
        currentDate={sailingProps.selectedDate}
        onSailingDayStar={sailingDayStarHandler}
        onSailingDayLock={sailingDayLockHandler}
        onSailingDayTemplateClick={handleDayTemplateFormClick}
        onSailingDayEditClick={handleDayTemplateEditClick}
        onAction={(action, sailing) => handleSailingAction(action, sailing, sailingProps.reload)}
        onRundownClick={(sailing: SimpleSailing, sailingDay: SimpleSailingDay) => {
          props.history.push(resolvePlannerPath(sailing.property._id, sailing._id, sailingDay._id));
        }}
        onContentBuilderClick={(sailing: SimpleSailing, sailingDay: SimpleSailingDay) => {
          props.history.push(resolveFeedPath(sailing.property._id, sailing._id, sailingDay._id));
        }}
        onAddDaysToBeginningClick={() => sailingProps.addDaysBefore()}
        onAddDaysToEndClick={() => sailingProps.addDaysAfter()}
        renderCustomActions={renderCustomActions}
      />
    );
  }

  async function handleSailingDayStar(
    sailing: SimpleSailing,
    sailingDay: SimpleSailingDay,
    updateSailingCallback: (sailingId: string) => Promise<void>
  ) {
    if (sailingDay.saved) {
      await deleteSailingDayTemplate(sailing._id, sailingDay._id);
    } else {
      if (!sailingDay.locked) {
        let continueSave = await Alert.question(
          'Saving a day will automatically lock it. Make sure you finish all changes first.\n' +
            'Do you want to continue?'
        );
        if (!continueSave) return;
      }
      await saveSailingDayAsTemplate(sailing._id, sailingDay._id);
    }

    return updateSailingCallback(sailing._id);
  }

  async function handleSailingDayLock(
    sailing: SimpleSailing,
    sailingDay: SimpleSailingDay,
    updateSailingCallback: (sailingId: string) => Promise<void>
  ) {
    // If we are unlocking a saved day warn that it will be unsaved as well
    if (sailingDay.locked) {
      if (sailingDay.saved) {
        let continueUnlocking = await Alert.question(
          'Unlocking a saved a day will also unsave it.\n' +
            'You can save it again after making changes.\n' +
            'Do you want to continue?'
        );
        if (!continueUnlocking) return;
      }
      await unlockSailingDay(sailing._id, sailingDay._id);
    } else {
      const { templateFragments } = sailingDay;

      if (templateFragments) {
        const toUnlink = FRAGMENT_TYPES.map(type => {
          const templateId = templateFragments[type];
          const unlinkStrategy: TemplateFragmentUnlinkStrategy = 'partial';

          if (!templateId) return undefined;

          return {
            sailingId: sailing._id,
            sailingDayId: sailingDay._id,
            fragmentType: type,
            templateId,
            unlinkStrategy,
          };
        }).filter(notUndefined);

        if (toUnlink.length) {
          const continueLocking = await Alert.question(
            'Locking a day will unlink all template fragments.\n' +
              'The day will no longer receive updates from the master template.\n' +
              'Do you want to continue?'
          );
          if (!continueLocking) return;
          await unlinkTemplateFragments(activeProperty._id, toUnlink);
        }
      }

      await lockSailingDay(sailing._id, sailingDay._id);
    }

    return updateSailingCallback(sailing._id);
  }

  async function handleSailingAction(
    action: DropdownItem,
    sailing: SimpleSailing,
    reloadSailings: () => Promise<void>
  ) {
    switch (action.key) {
      case 'apply':
        handleTemplateFormClick(sailing);
        break;
      case 'publish':
        await publishSailing(sailing._id);
        return reloadSailings();
      case 'voyage-csv':
        await handleExportVoyageData(sailing, 1);
        break;
      case 'voyage-csv-2':
        await handleExportVoyageData(sailing, 2);
        break;
      case 'save':
        await saveSailingAsTemplate(sailing._id);
        return reloadSailings();
    }
  }

  const selectedYear = new Date().getFullYear();

  return (
    <PageWrapper {...props}>
      {templateBundle ? (
        <SailingProvider
          propertyId={activeProperty._id}
          year={selectedYear}
          date={queryStrings.date}
          daysBefore={queryStrings.daysBefore}
          daysAfter={queryStrings.daysAfter}
          onAddDaysBefore={handleOnAddDaysBefore}
          onAddDaysAfter={handleOnAddDaysAfter}
          onChangeDate={handleOnChangeDate}
          sailingPopulator={(bs: BareSimpleSailing) =>
            populateSimpleSailing(
              bs,
              activeProperty,
              templateBundle.dayTemplates,
              templateBundle.sailingTemplates
            )
          }
          onNewSailings={handleOnNewSailings}
        >
          {sailingProps => {
            let sailings = sailingProps.sailings;

            let SailingsComponent = getSailingsComponent(sailingProps, sailings);

            return (
              <Page className="Itinerary absolute flex items-stretch">
                <MenuBar {...props} property={activeProperty} />
                <div className="SideMenu flex flex-col bg-navy-darkest shadow-md">
                  <div className="bg-white flex flex-col items-center">
                    <BrandLogo logo={activeProperty.brand.logo} />
                    <PropertyBanner {...props} property={activeProperty} swappable />
                  </div>
                  <div className="relative flex flex-col items-stretch p-4 bg-navy-darker">
                    <div ref={additionalActionsRef} className="grid gap-2">
                      <div className="flex items-stretch rounded-lg overflow-hidden bg-navy border border-navy-darkest">
                        <div className="flex-grow px-4 py-2">
                          <BubbleDebounce
                            onTrigger={[(date: string) => sailingProps.changeDate(date), 500]}
                          >
                            {({ debounce }) => (
                              <BubbleInput
                                type="date"
                                block
                                appearance="bare"
                                inputClassName="bg-navy text-white inverse-indicator"
                                value={sailingProps.selectedDate}
                                onValue={debounce}
                              />
                            )}
                          </BubbleDebounce>
                        </div>
                        {sailingProps.selectedDate !== DEFAULT_SAILING_DATE && (
                          <BubbleButton
                            variant="navy"
                            appearance="squared"
                            className="-ml-4 pl-2-i"
                            icon="rotate-left"
                            onClick={() => sailingProps.changeDate(DEFAULT_SAILING_DATE)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {canEditItineraryInfo(user, serverInfo) && (
                    <BubbleAccordion
                      variant="navy"
                      className="h-full flex flex-col border-t border-navy-darkest"
                      labels={['Manage']}
                      singleMode
                      open={[0]}
                      locked={[0]}
                    >
                      <div className="grid p-4 gap-4">
                        <BubbleButton
                          variant="slate"
                          linkProps={{
                            to: resolveItineraryManagementPath(activeProperty._id),
                          }}
                          icon="calendar-lines-pen"
                        >
                          Edit Itineraries
                        </BubbleButton>
                      </div>
                    </BubbleAccordion>
                  )}
                </div>
                <div
                  className="ItineraryContent flex-grow bg-blue-lightest flex
              flex-col overflow-y-auto"
                >
                  {SailingsComponent}
                </div>
                {showTemplateForm && activeProperty && (
                  <BubbleDrawer onClose={handleFormClose} containerClassName="bg-eggshell">
                    {selectedSailing && (
                      <TemplateLinkForm
                        mode="bulk"
                        user={user}
                        property={activeProperty}
                        templates={templateBundle.dayTemplates}
                        sailing={bareSimpleSailing(selectedSailing)}
                        onClose={handleFormClose}
                        onTemplateNameClick={(block: FragmentBlockSync) => {
                          props.history.push(
                            block.type === 'feed'
                              ? resolveTemplateFeedPath(activeProperty._id, block.templateId)
                              : resolveTemplatePlannerPath(activeProperty._id, block.templateId)
                          );
                        }}
                        onSailingDayNameClick={(
                          sailing: BareSimpleSailing,
                          sailingDay: BareSimpleSailingDay
                        ) => {
                          props.history.push(
                            resolvePlannerPath(sailing.property, sailing._id, sailingDay._id)
                          );
                        }}
                        onCreateTemplateClick={handleOnCreateTemplateFormClick}
                        onUnlinkTemplateFragments={linkFragments =>
                          handleOnUnlinkTemplateFragments(linkFragments, sailingProps.reload)
                        }
                        onLinkTemplateFragments={linkFragments =>
                          handleOnFinishLinkTemplateFragments(linkFragments, sailingProps.reload)
                        }
                      />
                    )}
                  </BubbleDrawer>
                )}
                {showDayTemplateForm && (
                  <BubbleDrawer
                    onClose={handleDayTemplateFormClose}
                    containerClassName="bg-eggshell"
                  >
                    {selectedSailingDay && selectedSailing && (
                      <TemplateLinkForm
                        mode="single"
                        user={user}
                        property={activeProperty}
                        templates={templateBundle.dayTemplates}
                        sailing={bareSimpleSailing(selectedSailing)}
                        sailingDay={bareSimpleSailingDay(selectedSailingDay)}
                        onClose={handleDayTemplateFormClose}
                        onTemplateNameClick={(block: FragmentBlockSync) => {
                          props.history.push(
                            block.type === 'feed'
                              ? resolveTemplateFeedPath(activeProperty._id, block.templateId)
                              : resolveTemplatePlannerPath(activeProperty._id, block.templateId)
                          );
                        }}
                        onSailingDayNameClick={(
                          sailing: BareSimpleSailing,
                          sailingDay: BareSimpleSailingDay
                        ) => {
                          props.history.push(
                            resolvePlannerPath(sailing.property, sailing._id, sailingDay._id)
                          );
                        }}
                        onCreateTemplateClick={handleOnCreateTemplateFormClick}
                        onUnlinkTemplateFragments={linkFragments =>
                          handleOnUnlinkTemplateFragments(linkFragments, sailingProps.reload)
                        }
                        onLinkTemplateFragments={linkFragments =>
                          handleOnFinishLinkTemplateFragments(linkFragments, sailingProps.reload)
                        }
                      />
                    )}
                  </BubbleDrawer>
                )}
                {showCreateTemplateForm && (
                  <Popup title="Create a Template from Day" onClose={handleCreateTemplateFormClose}>
                    {createTemplateForm && (
                      <>
                        <hr />
                        <div className="p-4" style={{ width: '500px' }}>
                          <div className="p-3 mb-3 bg-eggshell shadow rounded border">
                            Creating a template from:{' '}
                            <span className="font-bold">
                              {createTemplateForm.sailing.name} (Day{' '}
                              {createTemplateForm.sailingDay.name})
                            </span>
                          </div>
                          <TemplateForm
                            submitButtonTitle="Create Template"
                            initialForm={createTemplateForm.templateData}
                            propertyTags={activeProperty.tags}
                            onSubmit={async (templateData: TemplateFormDataBase) => {
                              await handleOnCreateTemplateFromSailingDay(templateData);
                              await refreshActiveProperty();
                            }}
                            onUpdatePropertyTags={async propertyTags => {
                              await updateTags(activeProperty._id, propertyTags);
                              await refreshActiveProperty();
                            }}
                          />
                        </div>
                      </>
                    )}
                  </Popup>
                )}
                {showDayEditForm && dayToEdit && (
                  <EditDayForm
                    hideItineraryInfo
                    dayToEdit={dayToEdit}
                    onClose={() => setShowDayEditForm(false)}
                    onSubmit={dayEditForm => {
                      handleOnSaveSailing(dayEditForm, sailingProps.reload);
                    }}
                    onEditCanvas={() => {
                      props.history.push(
                        resolvePlannerPath(
                          activeProperty._id,
                          dayToEdit.sailing._id,
                          dayToEdit.day._id
                        )
                      );
                    }}
                    onEditFeed={() => {
                      props.history.push(
                        resolveFeedPath(
                          activeProperty._id,
                          dayToEdit.sailing._id,
                          dayToEdit.day._id
                        )
                      );
                    }}
                  />
                )}
              </Page>
            );
          }}
        </SailingProvider>
      ) : (
        <BubbleOverlay className="h-full" loading />
      )}
    </PageWrapper>
  );
}
